<template>
    <div v-if="hasData" class="journey-info">
        <div class="journey-info-first">
            <slot />

            <div>
                <span v-if="hasTime" class="time" v-tippy="t({ key: 'shared.travel_time' })">
                    {{ time }}
                </span>
                <span v-if="hasTime && label" class="separator"> &#124; </span>
                <span v-if="label" class="number">
                    {{ label }}
                </span>
            </div>

            <div class="company">
                <LazyImage v-if="logo" :src="logo" :alt="name ? name : ''" v-tippy="name" />
                <span v-if="name" class="name">{{ formattedName }}</span>
                <span v-if="ferryName" class="name">{{ ferryName }}</span>

                <div v-if="labels" class="labels">
                    <span v-for="(infoLabel, index) in labels" :key="index">
                        {{ infoLabel }}
                    </span>
                </div>
            </div>
        </div>

        <div v-if="hasTicketType" class="ticket-type">
            <span class="ticket-type-label"> {{ t({ key: 'shared.ticket_type' }) }}: </span>
            {{ ticketType }}
        </div>

        <div v-if="hasTechnicalStop" class="journey-info-second">
            <span v-if="technicalStopLabel">
                {{ technicalStopLabel }}
            </span>
        </div>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, PropType } from 'vue';
    import { LabelFormat } from '@shared/interfaces/translation';
    import LazyImage from '@ui/components/LazyImage/LazyImage.vue';

    export default defineComponent({
        name: 'Info',
        methods: { t },
        components: { LazyImage },
        props: {
            label: String as PropType<string | null>,
            labels: Array as PropType<string[] | null>,
            time: String as PropType<string | null>,
            name: String as PropType<string | null>,
            ferryName: String as PropType<string | null>,
            logo: String as PropType<string | null>,
            ticketType: String as PropType<string | null>,
            technicalStop: Boolean as PropType<boolean | null>,
        },
        setup(props) {
            const hasData = computed(
                () =>
                    props.label ||
                    (props.labels && props.labels.length > 0) ||
                    props.time ||
                    props.logo ||
                    props.technicalStop ||
                    props.ticketType
            );

            const hasTime = computed(() => {
                if (!props.time) {
                    return false;
                }

                const timeString = props.time.trim();
                const timeRegex = /(?:(\d+)h)?(?:\s*(\d+)m)?/; // capture hours and minutes
                const match = timeString.match(timeRegex);

                if (!match) {
                    return false;
                }

                const hours = match[1] ? Number(match[1]) : 0;
                const minutes = match[2] ? Number(match[2]) : 0;

                return hours * 60 + minutes > 0;
            });

            const hasTechnicalStop = computed(() => Boolean(props.technicalStop));

            const hasTicketType = computed(() => Boolean(props.ticketType));

            const technicalStopLabel = computed(() => {
                if (hasTechnicalStop.value) {
                    return [
                        t({
                            key: 'shared.note',
                        }),
                        t({
                            key: 'shared.flight_intermediate_landing',
                            format: LabelFormat.Capitalize,
                        }),
                    ].join(' ');
                }
                return '';
            });

            const formattedName = computed(() =>
                props.ferryName ? `${props.name},` : props.name
            );

            return {
                hasTime,
                hasData,
                hasTechnicalStop,
                hasTicketType,
                technicalStopLabel,
                formattedName,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './Info.scss';
</style>
